import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridReact } from 'ag-grid-react';
import React, { useCallback, useMemo, useRef } from 'react';
import { toast } from 'react-toastify';
import ApiService from '../../../../services/apiService';
import { FaEdit, FaTrash } from 'react-icons/fa';

const CurrencyGrid = ({ data = [], loading, onEditCurrency, onDeleted }) => {
  const gridRef = useRef();

  const deleteRow = async (id) => {
    if (!id) return;
    const ok = window.confirm('Delete this currency? This action cannot be undone.');
    if (!ok) return;

    try {
      const api = new ApiService();
      const res = await api.deleteBody('DELETE_CURRENCY', { id: Number(id) });
      if (res?.data?.statusCode === 204 || res?.data?.statusCode === 200) {
        toast.success(res?.data?.message || 'Deleted');
        onDeleted?.(); // refresh list in parent
      } else {
        toast.warning(res?.data?.message || 'Delete failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Error deleting currency');
    }
  };

  const colDefs = useMemo(
    () => [
      {
        headerName: 'Name',
        field: 'name',
        minWidth: 180,
        flex: 1,
        cellRenderer: (p) => <span style={{ fontWeight: 600, color: '#343a40' }}>{p.value}</span>,
      },
      {
        headerName: 'Code',
        field: 'code',
        minWidth: 120,
        valueFormatter: (p) => (p.value || '').toUpperCase(),
        cellStyle: { textAlign: 'center', fontWeight: 700, color: '#6E5199' },
      },
      {
        headerName: 'Conversion Rate',
        field: 'conversionRate',
        minWidth: 160,
        valueFormatter: (p) => Number(p.value || 0).toLocaleString(undefined, { maximumFractionDigits: 6 }),
        cellStyle: { textAlign: 'right', color: '#28a745', fontWeight: 700 },
      },
      {
        headerName: 'Default',
        field: 'default',
        minWidth: 120,
        cellRenderer: (p) =>
          p.value ? (
            <span className="badge" style={{ background: '#28a745', color: '#fff', fontWeight: 700 }}>
              DEFAULT
            </span>
          ) : (
            <span className="badge" style={{ background: '#e9ecef', color: '#6c757d' }}>
              —
            </span>
          ),
        cellStyle: { textAlign: 'center' },
      },
      {
        headerName: 'Actions',
        field: 'id',
        minWidth: 140,
        pinned: 'right',
        cellRenderer: (props) => (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button className="btn btn-sm btn-outline-primary" onClick={() => onEditCurrency?.(props.data)} title="Edit">
              <FaEdit />
            </button>
            <button className="btn btn-sm btn-outline-danger" onClick={() => deleteRow(props.value)} title="Delete">
              <FaTrash />
            </button>
          </div>
        ),
        cellStyle: { textAlign: 'center' },
      },
    ],
    [onEditCurrency]
  );

  const onRowDoubleClicked = useCallback(
    (e) => {
      onEditCurrency?.(e.data);
    },
    [onEditCurrency]
  );

  return (
    <div
      className="ag-theme-alpine"
      style={{
        height: 460,
        border: '1px solid #e9ecef',
        borderRadius: 10,
        overflow: 'hidden',
        background: '#fff',
      }}
    >
      <AgGridReact
        ref={gridRef}
        rowData={data}
        columnDefs={colDefs}
        animateRows
        pagination
        paginationPageSize={20}
        rowSelection="single"
        onRowDoubleClicked={onRowDoubleClicked}
        overlayLoadingTemplate={`<span class="ag-overlay-loading-center">Loading...</span>`}
        loading={loading}
        defaultColDef={{
          sortable: true,
          filter: true,
          resizable: true,
          floatingFilter: true,
        }}
        getRowStyle={(p) => ({ fontSize: 13 })}
      />
    </div>
  );
};

export default CurrencyGrid;
